import { replacePropertiesInExpression } from '@gosupersimple/penguino';

import {
  getCell,
  getCellByPipelineIdOrThrow,
  isCellWithPipeline,
  isCellWithVisualisations,
  isRecordsCell,
  setCellPipeline,
  setCellVisualisations,
} from '@/core/cell';
import { setPipelineOperation } from '@/core/pipeline';
import { getPipelineStateAtIndex, PipelineStateContext } from '@/explore/pipeline/state';
import { getOperationPropertyKeys } from '@/explore/pipeline/operation';

import {
  getInstanceChildPipelineCells,
  getNonInstanceChildPipelineCells,
} from '@/explore/pipeline/utils';

import { getPipelineByIdOrThrow, setExplorationCell } from '@/core/exploration';
import { getCellPipeline } from '@/explore/utils';
import { notNil } from '@/lib/utils';
import { ensureUniqueKey } from '@/core/operation';

import {
  Aggregation,
  CompositeFilterCondition,
  Exploration,
  FilterCondition,
  isAggregatedVisualisation,
  Pipeline,
  PipelineOperation,
  Visualisation,
  VisualisationViewOptions,
} from '../types';
import {
  isFilteringAggregation,
  isKeyedAggregation,
  isSingleFilterCondition,
  isSortedAggregation,
  isValueExpression,
} from '../pipeline/operation';

export const updateFilterConditionKeys = (
  condition: CompositeFilterCondition,
  oldKey: string,
  newKey: string,
): CompositeFilterCondition => {
  if (isSingleFilterCondition(condition)) {
    return updateSingleFilterConditionKeys(condition, oldKey, newKey);
  }

  return {
    ...condition,
    operands: condition.operands.map((operand) =>
      updateFilterConditionKeys(operand, oldKey, newKey),
    ),
  };
};

const updateSingleFilterConditionKeys = (
  condition: FilterCondition,
  oldKey: string,
  newKey: string,
): FilterCondition => {
  return {
    ...condition,
    key: condition.key === oldKey ? newKey : condition.key,
  };
};

export const updateFilterConditionValueReferences = (
  condition: CompositeFilterCondition,
  oldKey: string,
  newKey: string,
): CompositeFilterCondition => {
  if (isSingleFilterCondition(condition)) {
    return updateSingleFilterConditionValueReferences(condition, oldKey, newKey);
  }

  return {
    ...condition,
    operands: condition.operands.map((operand) =>
      updateFilterConditionValueReferences(operand, oldKey, newKey),
    ),
  };
};

const updateSingleFilterConditionValueReferences = (
  condition: FilterCondition,
  oldKey: string,
  newKey: string,
): FilterCondition => {
  let value = condition.value;
  if (isValueExpression(value)) {
    value = {
      ...value,
      expression: replacePropertiesInExpression(value.expression, [{ prev: oldKey, next: newKey }]),
    };
  }

  return {
    ...condition,
    value,
  };
};

/**
 * Update field key references in an aggregation.
 */
const updateAggregationFieldReferences = (
  aggregation: Aggregation,
  oldKey: string,
  newKey: string,
): Aggregation => {
  let result = { ...aggregation };

  if (isKeyedAggregation(result) && result.key === oldKey) {
    result = { ...result, key: newKey };
  }

  if (isSortedAggregation(result)) {
    result = {
      ...result,
      sort: result.sort.map((s) => {
        if (s.key !== oldKey) {
          return s;
        }
        return { ...s, key: newKey };
      }),
    };
  }

  if (result.type === 'custom') {
    result = {
      ...result,
      value: {
        ...result.value,
        expression: replacePropertiesInExpression(result.value.expression, [
          { prev: oldKey, next: newKey },
        ]),
      },
    };
  }

  if (isFilteringAggregation(result) && result.filter !== undefined) {
    result = {
      ...result,
      filter: updateFilterConditionKeys(result.filter, oldKey, newKey),
    };
  }

  return result;
};

/**
 * Update all field key references in an operation.
 * The operation is assumed to be in the "current" pipeline and we want to
 * rename all references to fields in our current pipeline.
 */
export const updateOperationFieldReferences = <T extends PipelineOperation>(
  operation: T,
  oldKey: string,
  newKey: string,
): T => {
  switch (operation.operation) {
    case 'filter':
      return {
        ...operation,
        parameters: updateFilterConditionKeys(operation.parameters, oldKey, newKey),
      };

    case 'groupAggregate': {
      const { groups, aggregations, slice } = operation.parameters;
      return {
        ...operation,
        parameters: {
          ...operation.parameters,
          groups: groups.map((g) => (g.key === oldKey ? { ...g, key: newKey } : g)),
          aggregations: aggregations.map((a) =>
            updateAggregationFieldReferences(a, oldKey, newKey),
          ),
          slice:
            slice !== undefined
              ? {
                  ...slice,
                  sort: slice.sort?.map((s) => (s.key === oldKey ? { ...s, key: newKey } : s)),
                }
              : undefined,
        },
      };
    }

    case 'deriveField':
      return {
        ...operation,
        parameters: {
          ...operation.parameters,
          value: {
            ...operation.parameters.value,
            expression: replacePropertiesInExpression(operation.parameters.value.expression, [
              { prev: oldKey, next: newKey },
            ]),
          },
        },
      };

    case 'relationAggregate':
      return {
        ...operation,
        parameters: {
          ...operation.parameters,
          filter:
            operation.parameters.filter !== undefined
              ? updateFilterConditionValueReferences(operation.parameters.filter, oldKey, newKey)
              : undefined,
        },
      };
    case 'addRelatedColumn':
      return operation;

    default:
      return operation;
  }
};

/**
 * Update all field key references in an operation in a related pipeline.
 * The operation is assumed to be in a "related" pipeline and we want to
 * rename all references to fields in our current pipeline.
 */
export const updateRelatedOperationFieldReferences = <T extends PipelineOperation>(
  operation: T,
  oldKey: string,
  newKey: string,
): T => {
  switch (operation.operation) {
    case 'relationAggregate': {
      const { aggregations, slice, filter } = operation.parameters;
      return {
        ...operation,
        parameters: {
          ...operation.parameters,
          aggregations: aggregations.map((a) =>
            updateAggregationFieldReferences(a, oldKey, newKey),
          ),
          slice:
            slice !== undefined
              ? {
                  ...slice,
                  sort: slice.sort?.map((s) => (s.key === oldKey ? { ...s, key: newKey } : s)),
                }
              : undefined,
          filter:
            filter !== undefined ? updateFilterConditionKeys(filter, oldKey, newKey) : undefined,
        },
      };
    }

    case 'addRelatedColumn':
      return {
        ...operation,
        parameters: {
          ...operation.parameters,
          columns: operation.parameters.columns.map((column) =>
            column.key === oldKey ? { ...column, key: newKey } : column,
          ),
        },
      };

    default:
      return operation;
  }
};

/**
 * Rename the output property key of an operation that produces fields.
 */
export const renameOperationPropertyKey = <T extends PipelineOperation>(
  operation: T,
  oldKey: string,
  newKey: string,
): T => {
  switch (operation.operation) {
    case 'deriveField':
      if (operation.parameters.key === oldKey) {
        return {
          ...operation,
          parameters: {
            ...operation.parameters,
            key: newKey,
          },
        };
      }
      return operation;

    case 'addRelatedColumn':
      return {
        ...operation,
        parameters: {
          ...operation.parameters,
          columns: operation.parameters.columns.map((column) =>
            column.property.key === oldKey
              ? { ...column, property: { ...column.property, key: newKey } }
              : column,
          ),
        },
      };

    case 'groupAggregate':
      return {
        ...operation,
        parameters: {
          ...operation.parameters,
          aggregations: operation.parameters.aggregations.map((agg) =>
            agg.property.key === oldKey
              ? { ...agg, property: { ...agg.property, key: newKey } }
              : agg,
          ),
        },
      };

    case 'relationAggregate':
      return {
        ...operation,
        parameters: {
          ...operation.parameters,
          aggregations: operation.parameters.aggregations.map((agg) =>
            agg.property.key === oldKey
              ? { ...agg, property: { ...agg.property, key: newKey } }
              : agg,
          ),
        },
      };

    default:
      return operation;
  }
};

/**
 * Resolve field key conflicts across all pipelines in an exploration.
 * When an operation is added or modified we assume that it does not conflict with existing fields
 * in the pipeline state (upstream). Hence, conflicts can only appear upstream (eg instance parent change).
 * We process pipelines top-down and in case of conflicts we rename the downstream field key and all references to it.
 *
 * So
 * ```
 * Pipeline A                                   | Pipeline B
 * - op0: ...                                   | - op0: ...
 * - op1: new field: "field1"  (conflict)       | - ...
 * - op2: new field: "field1"                   |
 * - op3: new field: "field1_0"   <-------------| - op7: uses "field1_0"
 * Pipeline A instance                          |
 * - op0: uses "field1_0"                       |
 * - op1: ...                     <-------------| - op9: uses "field1_0" via instance
 * ```
 *
 * Becomes
 * ```
 * Pipeline A                                   | Pipeline B
 * - op0: ...                                   | - op0: ...
 * - op1: new field: "field1"                   | - ...
 * - op2: new field: "field1_0"                 |
 * - op3: new field: "field1_1"   <-------------| - op7: uses "field1_1"
 * Pipeline A instance                          |
 * - op0: uses "field1_1"                       |
 * - op1: ...                     <-------------| - op9: uses "field1_1" via instance
 * ```
 *
 */
export const resolveFieldConflicts = (
  exploration: Exploration,
  ctx: Omit<PipelineStateContext, 'exploration'>,
): Exploration => {
  const pipelineIds = exploration.view.cells
    .filter(isCellWithPipeline)
    .map((cell) => cell.pipeline.pipelineId);
  return pipelineIds.reduce<Exploration>((exp, pipelineId) => {
    return resolveFieldConflictsInPipeline(pipelineId, exp, ctx);
  }, exploration);
};

/**
 * Discover conflicts and call resolveDownstreamConflictsForKey on them.
 */
export const resolveFieldConflictsInPipeline = (
  pipelineId: string,
  exploration: Exploration,
  ctx: Omit<PipelineStateContext, 'exploration'>,
): Exploration => {
  const pipeline = getPipelineByIdOrThrow(pipelineId, exploration);

  return pipeline.operations.reduce<Exploration>((acc, operation, index) => {
    const updatedPipeline = getPipelineByIdOrThrow(pipelineId, acc);

    const state = getPipelineStateAtIndex(updatedPipeline, index, { ...ctx, exploration: acc });

    const operationKeys = getOperationPropertyKeys(operation);
    const conflictingKeys = operationKeys.filter((key) => state.fields.some((f) => f.key === key));

    return conflictingKeys.reduce<Exploration>((innerAcc, conflictingKey) => {
      return resolveDownstreamConflictsForKey(pipelineId, index, conflictingKey, innerAcc, ctx);
    }, acc);
  }, exploration);
};

/**
 * Resolve conflicts for a specific field key downstream of a given pipeline operation index.
 * Renames the field key and updates all references in downstream operations and related pipelines.
 *
 * This recursively "makes room" for the new field key by renaming would-be conflicting keys downstream.
 */
export const resolveDownstreamConflictsForKey = (
  pipelineId: string,
  startIndex: number,
  conflictingKey: string,
  exploration: Exploration,
  ctx: Omit<PipelineStateContext, 'exploration'>,
): Exploration => {
  let updatedExploration = exploration;

  let pipeline = getPipelineByIdOrThrow(pipelineId, updatedExploration);

  for (let i = startIndex; i < pipeline.operations.length; i++) {
    pipeline = getPipelineByIdOrThrow(pipelineId, updatedExploration);

    let operation = pipeline.operations[i];
    const state = getPipelineStateAtIndex(pipeline, i, { ...ctx, exploration: updatedExploration });

    // If the field is no longer present in the state, we can stop
    if (!state.fields.some((f) => f.key === conflictingKey)) {
      return updatedExploration;
    }

    const operationKeys = getOperationPropertyKeys(operation);
    if (!operationKeys.includes(conflictingKey)) {
      continue;
    }

    // Rename key
    const uniqueKey = ensureUniqueKey(
      state.fields.map((f) => f.key),
      conflictingKey,
    );
    operation = renameOperationPropertyKey(operation, conflictingKey, uniqueKey);

    updatedExploration = setOperationInExploration(
      updatedExploration,
      pipeline.pipelineId,
      operation,
      i,
    );

    // Make room for uniqueKey downstream before updating references.
    // Now that op[i] produces uniqueKey, it will appear in downstream states
    // and any downstream op also producing uniqueKey will be resolved first.
    updatedExploration = resolveDownstreamConflictsForKey(
      pipelineId,
      i + 1,
      uniqueKey,
      updatedExploration,
      ctx,
    );

    // Update references in current pipeline and related pipelines
    updatedExploration = updateFieldReferences(
      updatedExploration,
      pipelineId,
      i + 1,
      conflictingKey,
      uniqueKey,
      ctx,
    );
  }

  // Process instances

  const instanceIds = getInstanceChildPipelineCells(updatedExploration, pipeline).map(
    (cell) => cell.id,
  );

  return instanceIds.reduce<Exploration>((acc, cellId) => {
    // Fetch instance pipeline in each iteration since it may have changed in last iteration
    const instance = getCell(acc.view.cells, cellId);
    if (instance === undefined || !isCellWithPipeline(instance)) {
      return acc;
    }
    return resolveDownstreamConflictsForKey(
      instance.pipeline.pipelineId,
      0,
      conflictingKey,
      acc,
      ctx,
    );
  }, updatedExploration);
};

const setOperationInExploration = (
  exploration: Exploration,
  pipelineId: string,
  operation: PipelineOperation,
  operationIndex: number,
): Exploration => {
  const pipeline = getPipelineByIdOrThrow(pipelineId, exploration);
  const updatedPipeline = setPipelineOperation(pipeline, operation, operationIndex);
  const cell = getCellByPipelineIdOrThrow(exploration.view.cells, pipeline.pipelineId);
  return setExplorationCell(exploration, setCellPipeline(cell, updatedPipeline));
};

/**
 * Update field key references in visualisation view options.
 */
const updateVisualisationViewOptionsFieldReferences = (
  viewOptions: VisualisationViewOptions | undefined,
  oldKey: string,
  newKey: string,
): VisualisationViewOptions | undefined => {
  if (viewOptions === undefined) {
    return undefined;
  }

  let result = { ...viewOptions };

  // Update series keys
  if (result.series !== undefined) {
    const updatedSeries: VisualisationViewOptions['series'] = {};
    for (const [key, value] of Object.entries(result.series)) {
      const updatedKey = key === oldKey ? newKey : key;
      updatedSeries[updatedKey] = value;
    }
    result = { ...result, series: updatedSeries };
  }

  // Update axes.right.keys
  if (result.axes?.right?.keys !== undefined) {
    result = {
      ...result,
      axes: {
        ...result.axes,
        right: {
          ...result.axes.right,
          keys: result.axes.right.keys.map((key) => (key === oldKey ? newKey : key)),
        },
      },
    };
  }

  // Update bigNumber keys
  if (result.bigNumber !== undefined) {
    result = {
      ...result,
      bigNumber: {
        ...result.bigNumber,
        key: result.bigNumber.key === oldKey ? newKey : result.bigNumber.key,
        ...('comparisonKey' in result.bigNumber && result.bigNumber.comparisonKey !== undefined
          ? {
              comparisonKey:
                result.bigNumber.comparisonKey === oldKey ? newKey : result.bigNumber.comparisonKey,
            }
          : {}),
      },
    };
  }

  return result;
};

/**
 * Update field key references in a visualisation.
 * This handles both simple and aggregated visualisations.
 */
const updateVisualisationFieldReferences = (
  visualisation: Visualisation,
  oldKey: string,
  newKey: string,
): Visualisation => {
  let result = { ...visualisation };

  // Update mainAxisKey
  if (result.mainAxisKey === oldKey) {
    result = { ...result, mainAxisKey: newKey };
  }

  // Update valueKeys
  if (result.valueKeys.includes(oldKey)) {
    result = {
      ...result,
      valueKeys: result.valueKeys.map((key) => (key === oldKey ? newKey : key)),
    };
  }

  // Update sort keys
  if (result.sort !== undefined) {
    result = {
      ...result,
      sort: result.sort.map((s) => (s.key === oldKey ? { ...s, key: newKey } : s)),
    };
  }

  // Update view options
  result = {
    ...result,
    viewOptions: updateVisualisationViewOptionsFieldReferences(result.viewOptions, oldKey, newKey),
  };

  // Update aggregation for aggregated visualisations
  if (isAggregatedVisualisation(result)) {
    const { groups, aggregations } = result.aggregation;

    const updatedGroups = groups.map((g) => (g.key === oldKey ? { ...g, key: newKey } : g));

    const updatedAggregations = aggregations.map((agg) =>
      updateAggregationFieldReferences(agg, oldKey, newKey),
    );

    result = {
      ...result,
      aggregation: {
        groups: updatedGroups,
        aggregations: updatedAggregations,
      },
    };
  }

  return result;
};

/**
 * Update field key references in all visualisations of a cell.
 */
const updateCellVisualisationsFieldReferences = (
  exploration: Exploration,
  pipelineId: string,
  oldKey: string,
  newKey: string,
): Exploration => {
  const cell = getCellByPipelineIdOrThrow(exploration.view.cells, pipelineId);

  if (!isCellWithVisualisations(cell) || cell.visualisations === undefined) {
    return exploration;
  }

  const updatedVisualisations = cell.visualisations.map((vis) =>
    updateVisualisationFieldReferences(vis, oldKey, newKey),
  );

  return setExplorationCell(exploration, setCellVisualisations(cell, updatedVisualisations));
};

/**
 * Update field key references in cell.sort.
 */
const updateCellSortFieldReferences = (
  exploration: Exploration,
  pipelineId: string,
  oldKey: string,
  newKey: string,
): Exploration => {
  const cell = getCellByPipelineIdOrThrow(exploration.view.cells, pipelineId);

  if (!isRecordsCell(cell) || cell.sort === undefined) {
    return exploration;
  }

  const updatedSort = cell.sort.map((s) => (s.key === oldKey ? { ...s, key: newKey } : s));
  return setExplorationCell(exploration, { ...cell, sort: updatedSort });
};

/**
 * Update field key references in cell.viewOptions.fieldOrder.
 */
const updateCellViewOptionsFieldReferences = (
  exploration: Exploration,
  pipelineId: string,
  oldKey: string,
  newKey: string,
): Exploration => {
  const cell = getCellByPipelineIdOrThrow(exploration.view.cells, pipelineId);
  const fieldOrder = cell.viewOptions?.fieldOrder;

  if (fieldOrder === undefined || !(oldKey in fieldOrder)) {
    return exploration;
  }

  const updatedFieldOrder = Object.fromEntries(
    Object.entries(fieldOrder).map(([key, value]) => [key === oldKey ? newKey : key, value]),
  );

  return setExplorationCell(exploration, {
    ...cell,
    viewOptions: { ...cell.viewOptions, fieldOrder: updatedFieldOrder },
  });
};

/**
 * Update all field key references downstream of a given pipeline operation index.
 */
const updateFieldReferences = (
  exploration: Exploration,
  pipelineId: string,
  startIndex: number,
  oldKey: string,
  newKey: string,
  ctx: Omit<PipelineStateContext, 'exploration'>,
): Exploration => {
  let updatedExploration = exploration;

  let pipeline = getPipelineByIdOrThrow(pipelineId, updatedExploration);

  for (let i = startIndex; i < pipeline.operations.length; i++) {
    pipeline = getPipelineByIdOrThrow(pipelineId, updatedExploration);

    let operation = pipeline.operations[i];
    const state = getPipelineStateAtIndex(pipeline, i, { ...ctx, exploration: updatedExploration });

    // If the field is no longer present in the state, we can stop
    if (!state.fields.some((f) => f.key === newKey)) {
      return updatedExploration;
    }

    // Update references in current pipeline
    operation = updateOperationFieldReferences(operation, oldKey, newKey);

    updatedExploration = setOperationInExploration(
      updatedExploration,
      pipeline.pipelineId,
      operation,
      i,
    );
  }

  // Update references in visualisations
  updatedExploration = updateCellVisualisationsFieldReferences(
    updatedExploration,
    pipelineId,
    oldKey,
    newKey,
  );

  // Update references in cell sort
  updatedExploration = updateCellSortFieldReferences(
    updatedExploration,
    pipelineId,
    oldKey,
    newKey,
  );

  // Update references in cell viewOptions.fieldOrder
  updatedExploration = updateCellViewOptionsFieldReferences(
    updatedExploration,
    pipelineId,
    oldKey,
    newKey,
  );

  // Update references in instances

  const instanceIds = getInstanceChildPipelineCells(updatedExploration, pipeline).map(
    (cell) => cell.id,
  );

  updatedExploration = instanceIds.reduce<Exploration>((acc, cellId) => {
    // Fetch instance pipeline in each iteration since it may have changed in last iteration
    const instance = getCell(acc.view.cells, cellId);
    if (instance === undefined || !isCellWithPipeline(instance)) {
      return acc;
    }
    return updateFieldReferences(acc, instance.pipeline.pipelineId, 0, oldKey, newKey, ctx);
  }, updatedExploration);

  // Update references in related pipelines

  const childPipelines = getNonInstanceChildPipelineCells(updatedExploration, pipeline)
    .map(getCellPipeline)
    .filter(notNil);

  return childPipelines.reduce<Exploration>((acc, childPipeline) => {
    const cell = getCellByPipelineIdOrThrow(acc.view.cells, childPipeline.pipelineId);
    const updatedPipeline = childPipeline.operations.reduce<Pipeline>((acc, operation, idx) => {
      switch (operation.operation) {
        case 'relationAggregate':
        case 'addRelatedColumn': {
          if (operation.parameters.pipelineId !== pipeline.pipelineId) {
            return acc;
          }
          const updatedOperation = updateRelatedOperationFieldReferences(operation, oldKey, newKey);
          return setPipelineOperation(acc, updatedOperation, idx);
        }
        default:
          return acc;
      }
    }, childPipeline);
    return setExplorationCell(acc, setCellPipeline(cell, updatedPipeline));
  }, updatedExploration);
};
